import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Link,
  Radio,
  RadioGroup,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { parseStudentCsv, rowLabel } from "./utils/studentCsv";
import { previewStudentCsv, applyStudentCsv } from "./api/studentCsv";
import { loadAppSettings, studentAgeRange, ageWarning, ageCheckUnavailableReason } from "../shared utils/appSettings";

// Upload a CSV of student details into the group: parse in the browser, ask the
// backend what each row would do (and who it might duplicate), let the user
// resolve, then apply in one transaction. Nothing is written before "Apply".
//
// Steps: pick → preview → (resolve) → done. The backend owns the matching
// (id → member, no id → next unnamed placeholder, leftovers → new member), so a
// second upload after a partial one lands the same way.
// arrivalDate: the group's start (YYYY-MM-DD) — ages are taken on that day.
export default function StudentCsvDialog({ open, onClose, groupId, token, arrivalDate, onApplied, onGoToBookings }) {
  const fileRef = useRef(null);
  // The accepted age range is a setting (student.age.min/max). When there's no
  // range the preview SAYS so — a silent column would look the same as a clean one.
  const [settingsState, setSettingsState] = useState(null);
  useEffect(() => {
    if (!open) return;
    loadAppSettings(token).then(setSettingsState);
  }, [open, token]);
  const ageRange = studentAgeRange(settingsState?.settings);
  const ageUnavailable = ageCheckUnavailableReason(settingsState);
  const ageNote = (dob) => ageWarning(dob, arrivalDate, ageRange);
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null); // { rows, errors, skippedExample }
  const [preview, setPreview] = useState(null); // backend preview response
  const [resolutions, setResolutions] = useState({}); // row → { resolution, existingStudentId }
  const [createSurplus, setCreateSurplus] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // backend apply response

  const reset = () => {
    setFileName("");
    setParsed(null);
    setPreview(null);
    setResolutions({});
    setCreateSurplus(false);
    setBusy(false);
    setError(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  // --- step 1: file → rows -------------------------------------------------------
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    setPreview(null);
    setResult(null);
    setResolutions({});
    setFileName(f.name);
    const text = await f.text();
    const p = parseStudentCsv(text);
    setParsed(p);
    if (!p.rows.length) return;
    // Straight into the preview — it's a dry run, and it's the whole point.
    setBusy(true);
    try {
      const pv = await previewStudentCsv(groupId, token, p.rows);
      setPreview(pv);
      // Every duplicate-flagged row starts as SKIP so a careless Apply merges no one.
      const init = {};
      (pv?.rows || []).forEach((r) => {
        if (Array.isArray(r.duplicates) && r.duplicates.length) {
          init[r.row] = { resolution: "SKIP", existingStudentId: r.duplicates[0].studentId };
        }
      });
      setResolutions(init);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  // --- derived --------------------------------------------------------------------
  const rowsByNo = useMemo(() => {
    const m = {};
    (parsed?.rows || []).forEach((r) => {
      m[r.row] = r;
    });
    return m;
  }, [parsed]);

  const previewRows = useMemo(() => preview?.rows || [], [preview]);
  const counts = useMemo(() => {
    const c = { update: 0, fill: 0, create: 0, error: 0, dup: 0 };
    previewRows.forEach((r) => {
      if (r.action === "UPDATE") c.update += 1;
      else if (r.action === "FILL_PLACEHOLDER") c.fill += 1;
      else if (r.action === "CREATE") c.create += 1;
      else if (r.action === "ERROR") c.error += 1;
      if (Array.isArray(r.duplicates) && r.duplicates.length) c.dup += 1;
    });
    return c;
  }, [previewRows]);

  // What Apply will actually do, given the resolutions and the surplus checkbox.
  const willDo = useMemo(() => {
    let writes = 0;
    let skipped = 0;
    previewRows.forEach((r) => {
      if (r.action === "ERROR") return;
      const res = resolutions[r.row]?.resolution;
      if (res === "SKIP") {
        skipped += 1;
        return;
      }
      if (r.action === "CREATE" && !createSurplus && res !== "USE_EXISTING") {
        skipped += 1;
        return;
      }
      writes += 1;
    });
    return { writes, skipped };
  }, [previewRows, resolutions, createSurplus]);

  const setResolution = (rowNo, patch) =>
    setResolutions((prev) => ({ ...prev, [rowNo]: { ...(prev[rowNo] || {}), ...patch } }));

  // --- step 3: apply --------------------------------------------------------------
  const handleApply = async () => {
    if (!preview || busy || willDo.writes === 0) return;
    setBusy(true);
    setError(null);
    try {
      const rows = previewRows
        .filter((r) => r.action !== "ERROR")
        .map((r) => {
          const src = rowsByNo[r.row];
          const res = resolutions[r.row];
          return {
            row: r.row,
            studentId: src?.studentId ?? null,
            fields: src?.fields || {},
            resolution: res?.resolution || "APPLY",
            existingStudentId: res?.resolution === "USE_EXISTING" ? res.existingStudentId : null,
          };
        });
      const out = await applyStudentCsv(groupId, token, rows, createSurplus);
      setResult(out);
      if (out?.ok) await onApplied?.();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  // --- render helpers --------------------------------------------------------------
  const actionChip = (r) => {
    switch (r.action) {
      case "UPDATE":
        return <Chip size="small" variant="outlined" label="Update" />;
      case "FILL_PLACEHOLDER":
        return <Chip size="small" variant="outlined" color="primary" label="Fill placeholder" />;
      case "CREATE":
        return <Chip size="small" variant="outlined" color="warning" label="New member" />;
      case "ERROR":
        return <Chip size="small" color="error" label="Error" />;
      default:
        return <Chip size="small" variant="outlined" label={r.action || "?"} />;
    }
  };

  const outcomeChip = (o) => {
    switch (o.outcome) {
      case "UPDATED":
        return <Chip size="small" color="success" label="Updated" />;
      case "LINKED_EXISTING":
        return <Chip size="small" color="success" label="Linked existing" />;
      case "CREATED":
        return <Chip size="small" color="success" label="Created" />;
      case "SKIPPED":
        return <Chip size="small" variant="outlined" label="Skipped" />;
      default:
        return <Chip size="small" variant="outlined" label={o.outcome || "?"} />;
    }
  };

  const dupLabel = (d) =>
    `${[d.studentName, d.studentSurname].filter(Boolean).join(" ") || `#${d.studentId}`}${
      d.studentNationality ? `, ${d.studentNationality}` : ""
    }${d.studentDob ? `, ${d.studentDob}` : ""}${
      ageNote(d.studentDob) ? ` ⚠ ${ageNote(d.studentDob)}` : ""
    } — student #${d.studentId}${
      d.alreadyInThisGroup
        ? " (already in this group)"
        : d.groups?.length
        ? ` in ${d.groups.map((g) => `“${g.groupName}”${g.year ? ` ${g.year}` : ""}`).join(", ")}`
        : ""
    }`;

  const noBookingsNote = "They'll have no bookings — copy a programme onto them on the Bookings tab afterwards.";

  const bookingsLink = onGoToBookings ? (
    <Link
      component="button"
      type="button"
      variant="body2"
      onClick={() => {
        handleClose();
        onGoToBookings();
      }}
    >
      Go to the Bookings tab
    </Link>
  ) : null;

  const renderPreviewRow = (r) => {
    const src = rowsByNo[r.row];
    const dups = Array.isArray(r.duplicates) ? r.duplicates : null;
    const res = resolutions[r.row];
    const surplusSkipped = r.action === "CREATE" && !createSurplus && res?.resolution !== "USE_EXISTING";
    return (
      <TableRow key={r.row} sx={r.action === "ERROR" || surplusSkipped ? { opacity: 0.6 } : undefined}>
        <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>{r.row}</TableCell>
        <TableCell sx={{ verticalAlign: "top" }}>
          {rowLabel(src)}
          {src?.fields?.studentDob ? (
            <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.6 }}>
              {src.fields.studentDob}
            </Typography>
          ) : null}
          {ageNote(src?.fields?.studentDob) ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={ageNote(src.fields.studentDob)}
              sx={{ ml: 0.5, height: 20, fontSize: 11 }}
            />
          ) : null}
        </TableCell>
        <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
          {actionChip(r)}
          {r.targetLabel && r.action !== "ERROR" ? (
            <Typography variant="caption" sx={{ display: "block", opacity: 0.6 }}>
              {r.targetLabel}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell sx={{ verticalAlign: "top" }}>
          {r.action === "ERROR" ? (
            <Typography variant="body2" color="error.main">
              {r.error}
            </Typography>
          ) : dups === null ? (
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              Not checked — no date of birth or nationality on the row
            </Typography>
          ) : dups.length === 0 ? (
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              —
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              <Typography variant="body2" color="warning.main">
                May already exist:
              </Typography>
              <RadioGroup
                value={res?.resolution || "SKIP"}
                onChange={(e) => setResolution(r.row, { resolution: e.target.value })}
              >
                {dups.map((d) => (
                  <FormControlLabel
                    key={d.studentId}
                    value="USE_EXISTING"
                    control={
                      <Radio
                        size="small"
                        checked={res?.resolution === "USE_EXISTING" && res?.existingStudentId === d.studentId}
                        onChange={() => setResolution(r.row, { resolution: "USE_EXISTING", existingStudentId: d.studentId })}
                      />
                    }
                    label={<Typography variant="body2">Use existing: {dupLabel(d)}</Typography>}
                  />
                ))}
                <FormControlLabel
                  value="CREATE_NEW"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      {r.action === "CREATE" ? "Create new anyway" : "Different person — fill the placeholder"}
                    </Typography>
                  }
                />
                <FormControlLabel value="SKIP" control={<Radio size="small" />} label={<Typography variant="body2">Skip this row</Typography>} />
              </RadioGroup>
            </Stack>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload student details from a CSV</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {!result && (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Fills the Personal, Other Info and Permissions tabs from a file. Rows with a{" "}
              <code>student_id</code> update that member; rows without one fill the group&apos;s
              unnamed placeholders in order. Blank cells leave the current value alone. Nothing
              is written until you click Apply.
            </Typography>
          )}

          {error ? <Alert severity="error">{String(error.message || error)}</Alert> : null}

          {/* Step 1: pick */}
          {!result && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={busy}>
                Choose CSV
                <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={handleFile} />
              </Button>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                {fileName || "No file chosen"}
              </Typography>
              {busy && !preview ? <CircularProgress size={18} /> : null}
            </Stack>
          )}

          {/* Parse problems */}
          {parsed && parsed.errors.length > 0 && (
            <Alert severity={parsed.rows.length ? "warning" : "error"}>
              {parsed.rows.length
                ? `${parsed.errors.length} row${parsed.errors.length === 1 ? "" : "s"} left out — fix and re-upload to include them:`
                : "Nothing could be read from the file:"}
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                {parsed.errors.map((e, i) => (
                  <li key={i}>
                    {e.row ? `Row ${e.row}: ` : ""}
                    {e.message}
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          {/* Step 2: preview */}
          {preview && !result && (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Typography variant="subtitle2">{previewRows.length} row{previewRows.length === 1 ? "" : "s"}:</Typography>
                {counts.update > 0 && <Chip size="small" variant="outlined" label={`${counts.update} update`} />}
                {counts.fill > 0 && <Chip size="small" variant="outlined" color="primary" label={`${counts.fill} fill placeholder`} />}
                {counts.create > 0 && <Chip size="small" variant="outlined" color="warning" label={`${counts.create} new member`} />}
                {counts.error > 0 && <Chip size="small" color="error" label={`${counts.error} error`} />}
                {counts.dup > 0 && <Chip size="small" color="warning" label={`${counts.dup} may already exist`} />}
                {parsed?.skippedExample ? (
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    (example row skipped)
                  </Typography>
                ) : null}
              </Stack>

              {counts.create > 0 && (
                <Alert severity="info" icon={false}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={createSurplus}
                        onChange={(e) => setCreateSurplus(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {counts.create} more student{counts.create === 1 ? "" : "s"} than the group has — add{" "}
                        {counts.create === 1 ? "them" : "them"} as new members?
                      </Typography>
                    }
                  />
                  <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
                    New members get a stay on the group dates and nothing else. {noBookingsNote}
                  </Typography>
                </Alert>
              )}

              {counts.dup > 0 && (
                <Alert severity="warning">
                  {counts.dup} row{counts.dup === 1 ? "" : "s"} match{counts.dup === 1 ? "es" : ""} a student already in the
                  system on name, nationality and date of birth. Each is set to <strong>Skip</strong> until you
                  choose — <em>Use existing</em> if it&apos;s the same person coming back, or create/fill anyway if
                  it&apos;s a coincidence.
                </Alert>
              )}

              {ageUnavailable && (
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  {ageUnavailable}
                </Typography>
              )}

              {Array.isArray(preview.membersNotInFile) && preview.membersNotInFile.length > 0 && (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {preview.membersNotInFile.length} member{preview.membersNotInFile.length === 1 ? " isn't" : "s aren't"} in
                  the file and will be left as {preview.membersNotInFile.length === 1 ? "they are" : "they are"}.
                </Typography>
              )}

              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Student</TableCell>
                      <TableCell>Will</TableCell>
                      <TableCell>Check</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{previewRows.map(renderPreviewRow)}</TableBody>
                </Table>
              </Box>
            </>
          )}

          {/* Step 3: result */}
          {result && (
            <>
              {result.ok ? (
                <Alert severity="success">
                  Applied. {result.rows.filter((o) => o.outcome !== "SKIPPED").length} of {result.rows.length} row
                  {result.rows.length === 1 ? "" : "s"} written.
                </Alert>
              ) : (
                <Alert severity="error">
                  Nothing was written.{result.row ? ` Row ${result.row}: ` : " "}
                  {result.error || "The upload was rejected."}
                  {result.row ? " Fix that row in the file and upload again." : ""}
                </Alert>
              )}
              {result.ok && result.rows.some((o) => o.note) && (
                <Alert severity="info">
                  Some members now have a stay but no bookings. {bookingsLink}
                </Alert>
              )}
              {result.ok && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Student</TableCell>
                      <TableCell>Outcome</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.rows.map((o) => (
                      <TableRow key={o.row}>
                        <TableCell>{o.row}</TableCell>
                        <TableCell>
                          {rowLabel(rowsByNo[o.row])}
                          {o.studentId ? (
                            <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.6 }}>
                              #{o.studentId}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{outcomeChip(o)}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {o.outcome === "LINKED_EXISTING" && o.removedPlaceholderId
                              ? `Placeholder #${o.removedPlaceholderId} replaced${
                                  o.movedBookings != null ? `, ${o.movedBookings} booking${o.movedBookings === 1 ? "" : "s"} moved` : ""
                                }. `
                              : ""}
                            {o.note || ""}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={busy || !preview || willDo.writes === 0}
          >
            {busy && preview
              ? "Applying…"
              : `Apply ${willDo.writes} row${willDo.writes === 1 ? "" : "s"}${willDo.skipped ? ` (${willDo.skipped} skipped)` : ""}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
